/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useState, useEffect } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import "../../Styles/styles-UploadData.css";
import { CheckBoxCard } from "./CheckBoxCard/CheckBoxCard";
import { DatePicker, Upload, Button, Checkbox, Select, Typography, Radio, Space, Tooltip } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { Reports } from "../Report/Report";
import Papa from 'papaparse';
import { DataActionSelection } from "./DataActionSelection";
import { audioMessage } from "../Common/Notifications/audioMessage";

const { Dragger } = Upload;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;
export const IntegrationUpload = ({ 
  dataProviders, 
  tabType, 
  activeTab, 
  providersByIntegration 
}) => {
  const trainingAPI = new TrainingAPI();
  const [checked, setChecked] = React.useState([]);
  const [csvData, setCsvData] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [dateRangeMoment, setDateRangeMoment] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [showLift, setShowLift] = useState(false);
  const [lift, setLift] = useState(false);
  const [liftType, setLiftType] = useState('');
  const [stepNum, setStepNum] = useState(1);
  const [reportParams, setReportParams] = useState({});
  const [usingPolar, setUsingPolar] = useState(false);
  const [customDate, setCustomDate] = useState(null);
  const [dataAction, setDataAction] = useState('overwrite')
  const [parseFullSecaCSV, setParseFullSecaCSV] = useState(false);

  const draggerProps = {
    name: 'file',
    multiple: false,
    onChange(info) {
      const { status } = info.file;
      if (status === 'done') {
        audioMessage.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        audioMessage.error(`${info.file.name} file upload failed.`);
      }
    },
    onRemove() {
      setCsvData({});
      setIsDisabled(true);
    }
  };

  const customRequest = ({ file, onSuccess, activeTab }) => {
    setFileList([file]);
  
    const reader = new FileReader();
    reader.onload = function(event) {
      const csvData = event.target.result;
  
      // Use PapaParse to parse CSV correctly
      const parsedData = Papa.parse(csvData, {
        delimiter: ",", // Detects delimiter automatically, but can be set explicitly
        header: false,  // Set to true if the first row contains headers
        skipEmptyLines: true, // Remove empty lines
        dynamicTyping: true, // Converts numeric strings to actual numbers
        quoteChar: '"', // Handles quoted values correctly
        transform: value => value.trim(), // Trim whitespace from each field
      });
  
      setCsvData(parsedData.data || []);
      console.log(parsedData.data || []);
      setIsDisabled(!(checked.length > 0 && parsedData.data));
    };
  
    reader.readAsText(file);
    onSuccess('ok'); // Simulate a successful upload without any network request
  };
  

  const handleNextClickStep1 = () => {
    let file_type = '';
    if (checked.some(item => providersByIntegration["gps"]?.includes(item))) {
      file_type = 'gps';
    } else if (checked.some(item => providersByIntegration["testing"]?.includes(item))) {
      file_type = 'testing';
    } else if (checked.some(item => providersByIntegration["forceplate"]?.includes(item))) {
      file_type = 'forceplate';
    } else if (checked.some(item => providersByIntegration["nutrition"]?.includes(item))) {
      file_type = 'nutrition';
    } else {
      file_type = 'misc';
    }

    // Store data in S3 only if its production
    let response_store_s3 = true;
    if (import.meta.env.MODE === "production") {
      response_store_s3 = storeS3(file_type);
    }

    if (response_store_s3) {
      // Prepare request param object to fetch report
      let reportSelection = {}
      reportSelection['upload_type'] = tabType;
      reportSelection['requested_sources'] = checked;
      reportSelection['start_date'] = dateRange[0];
      reportSelection['end_date'] = dateRange[1];
      if (showLift) {
        reportSelection['add_lift'] = lift;
        reportSelection['lift_type'] = liftType;
      }
      reportSelection['csv_data'] = csvData;
      reportSelection['customDate'] = customDate;
      reportSelection['data_action'] = dataAction
      reportSelection['parse_full_seca_csv'] = parseFullSecaCSV;
      setReportParams(reportSelection)

      setStepNum(2);
    }
    
  }

  const onCustomDateChange = (date, dateString) => {
    setCustomDate(date);
  }

  const storeS3 = (
    file_type
  ) => {
    if (file_type && fileList.length >= 1) {
      const file = fileList[0];

      if (file) 
      {
        trainingAPI
        .storeS3V2(
          file_type, file
        )
        .then((data) => {
          console.log("data stored in S3");
          return true;
        })
        .catch((data) => {
          console.error("errors storing data in S3");
          return false;
        });
      }
    }
  
    return true;
  };

  const handleCheckProvider = (providerKey) => {
    let newCheckedVal = [];
    if (checked.includes(providerKey)) {
      // Uncheck Flow
      newCheckedVal = checked.filter(ch => ch != providerKey);
      setChecked(newCheckedVal);
    } else {
      // Check Flow
      if (tabType === "csv" && checked.length === 1) {
        audioMessage.error("You can't select more than one third party for CSV upload.");
      } else {
        newCheckedVal = [...checked, providerKey];
        setChecked(newCheckedVal);
      }
    }

    setUsingPolar(newCheckedVal.includes("polar"));
    
    // Change Next Button Step 1 Disabled State
    if (tabType === "csv") {
      setIsDisabled(!(newCheckedVal.length > 0 && csvData.length > 0));
    }
    else if (tabType === "api"){
      setIsDisabled(!(newCheckedVal.length > 0 && dateRange.length > 0 && dateRange.every(item => ![''].includes(item))));
    }
    
    updateShowLift(newCheckedVal);

    setReportParams({...reportParams, requested_sources: newCheckedVal});
  }

  const updateShowLift = (checked) => {
    if (checked.length === 0){
      setLift(false);
      setShowLift(false);
    }

    const hasSelectedGPSProvider = checked.some(item => providersByIntegration["gps"]?.includes(item));

    if (hasSelectedGPSProvider) {
      setLift(false);
      setShowLift(true);
    }
    else {
      setLift(false);
      setShowLift(false);
    }
  }

  const dateRangeSelectionChange = (dates, dateStrings) => {
    setDateRangeMoment(dates);
    setDateRange(dateStrings);
    setIsDisabled(!(checked.length > 0 && dateStrings.length > 0 && dateStrings.every(item => ![''].includes(item))));
  }

  const handleLiftCheckChange = (liftChecked) => {
    setLift(liftChecked);
    setLiftType('');
  }

  const handlePrevClick = () => {
    setStepNum(1);
  }

  const onLiftTypeChange = (value) => {
    setLiftType(value);
  }

  const determineShowDataAction = () => {
    const notAllowedProviders = ["catapult-vector-core", "catapult", "vert"]

    return dataProviders?.map(e => !notAllowedProviders.includes(e)).every(e => e === true)
  }

  const getLiftAndButton = () => {
    return (
      <>
        {usingPolar ? (
          <div style={{ marginTop: "10px" }}>
            <Text strong>Custom Date (If not provided in file): </Text>
            <DatePicker
              value={customDate}
              onChange={onCustomDateChange}
            />
          </div>
        ) : null}
        {
          showLift ? (
            <div className="lift-checkbox">
              <Checkbox 
                onChange={e => handleLiftCheckChange(e.target.checked)}
                checked={lift}
              >
                Lift?
              </Checkbox>
              {
                lift ? (
                  <div className="lift-type-dropdown">
                    <Select
                      placeholder="Choose Lift Type"
                      value={liftType}
                      style={{ width: 200 }}
                      onChange={onLiftTypeChange}
                    >
                      <Option value="Recovery">Recovery</Option>
                      <Option value="Speed">Speed</Option>
                      <Option value="Hard">Hard</Option>
                    </Select>
                  </div>
                ) : null
              }
            </div>
          ) : null
        }

        <Button 
          type="primary" 
          onClick={handleNextClickStep1}
          disabled={isDisabled}
          className="next-btn"
        >
          Next
        </Button>
      </>
    )
  }

  const getSecaExtraOptions = () => {
    return (
      <>

      { checked.length > 0 && checked.includes('seca') ? 


        <div
            style={{
                display: "flex",
                alignContent: "baseline",
                justifyContent: "center",
                width: "100%",
                columnGap: "2em",
                alignItems: "center",
                marginTop: '1em'
            }}
        >
            <Radio.Group
                onChange={(e) => { setParseFullSecaCSV(e?.target?.value) }}
                value={parseFullSecaCSV}
                optionType="button"
            >
                <Space direction="horizontal" style={{ height: "auto" }}>
                  <Tooltip title="Smart filtering to only include the relatively new datapoints in the file.">
                    <Radio
                        value={false}
                        style={{ height: "auto", whiteSpace: "nowrap" }}
                    >
                        <div style={{ lineHeight: 1.5 }}> Smart Import
                            <small style={{ display: "block" }}>
                                Faster. Better for everyday use.
                            </small>
                        </div>
                    </Radio>
                    </Tooltip>

                    <Radio
                        value={true}
                        style={{
                            height: "auto",
                            whiteSpace: "nowrap",
                            marginLeft: "-1em",
                        }}
                    >
                        <div style={{ lineHeight: 1.5 }}>Full File Import
                            <small style={{ display: "block" }}>
                                Slower. Use for multiple years of data
                            </small>
                        </div>
                    </Radio>
                </Space>
            </Radio.Group>
        </div>
         : null }
          </>
    )
  }

  const PostSubmitReset = () => {
    setChecked([]);
    setCsvData([]);
    setFileList([]);
    setDateRange([]);
    setDateRangeMoment([]);
    setIsDisabled(true);
    setShowLift(false);
    setLift(false);
    setLiftType('');
    setReportParams({});
    setUsingPolar(false);
    setCustomDate(null);
  }

  useEffect(() => {
    setReportParams((prev) => ({ ...prev, parseFullSecaCSV: parseFullSecaCSV }));
  }, [parseFullSecaCSV]);

  useEffect(() => {
    setStepNum(1);
    PostSubmitReset();
  }, [activeTab]);
  
  return (
    <>
      <div className="integration-container">
        <div className="providers-container">
          {
            dataProviders && (
              dataProviders.map((provider, i) => {
                let providerKey = provider.key;
                return (
                  <CheckBoxCard 
                    display_name={provider.name} 
                    name={providerKey}
                    key={i} 
                    handleCheckProvider={handleCheckProvider}
                    defaultValue={checked.includes(providerKey)}  
                  > 
                  {provider.url ? <img src={provider.url} alt={`${providerKey}_logo`} className="upload-card__logo"/> : <>{provider.name}</>}
                    
                  </CheckBoxCard>
                )
              })
            )
          }
        </div>

        {
          tabType === "csv" 
          ?
          (
            stepNum === 1 
            ?
            (
              <div className="common-upload">
                <Text style={{marginBottom: '1em', fontSize: 22}}>Upload your data</Text>

                {determineShowDataAction() && <DataActionSelection dataAction={dataAction} setDataAction={setDataAction} />}

                <Dragger 
                  {...draggerProps} 
                  className="csv-upload"
                  maxCount={1}
                  accept=".csv"
                  customRequest={customRequest} 
                  fileList={fileList}
                  itemRender={() => {
                    return <div />
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint">
                    The file must be in .csv format.
                  </p>
                  <div style={{height: '20px'}}>
                    {fileList && fileList.length > 0 && (
                      <div>
                        {fileList.map((file) => (
                          <div key={file.uid}>
                            {file.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Dragger>
                <div>
                  {getSecaExtraOptions()}
                </div>
                <div>
                  {getLiftAndButton()}
                </div>
              </div>
            )
            :
            (
              <Reports
                isSkippable={true} 
                handlePrevClick={handlePrevClick} 
                stepNum={stepNum}
                reportParams={reportParams}
                PostSubmitReset={PostSubmitReset}
              />
            )
          )
          :
          (
            stepNum === 1 
            ?
            (
              <div className="common-upload range-picker">
                <Text style={{marginBottom: '2em', fontSize: 22}}>Select a date range to import data</Text>
                <RangePicker 
                  onChange={dateRangeSelectionChange} 
                  value={dateRangeMoment}
                />

              {getLiftAndButton()}
              </div>
            )
            :
            (
              <Reports 
                isSkippable={true} 
                dateRange={dateRangeMoment}
                handlePrevClick={handlePrevClick} 
                stepNum={stepNum} 
                reportParams={reportParams}
                PostSubmitReset={PostSubmitReset}
              />
            )
          )
        }
      </div>
      
      {/* <Footer style={{color: 'grey', marginTop:'70px'}}>GPS DataViz 2021</Footer> */}
    </>
  );
}